<template>
  <MDBContainer class="p-0">
    <Loader :show="loading" v-if="loading" />
    <MDBCard v-else>
      <MDBCardBody>
        <div class="d-flex justify-content-between align-items-center mb-5">
          <MDBCardTitle class="mb-0">{{
            $t("shop.orderDelivery")
          }}</MDBCardTitle>
          <MDBBtn color="danger" class="text-uppercase flex-shrink-0" @click="openNewAddressModal">
            {{ $t("global.buttons.newAddress") }}
          </MDBBtn>
        </div>
        <MDBCardText class="pb-0">
          <div v-if="hasDeletedItems" class="alert alert-warning mb-4">
            <h6 class="mb-2">{{ $t("shop.deletedProductsTitle") }}</h6>
            <p class="mb-2">{{ $t("shop.deletedProductsMessage") }}</p>
            <ul class="mb-0 ps-3">
              <li v-for="(name, index) in deletedItemNames" :key="`deleted_${index}`">{{ name }}</li>
            </ul>
          </div>
          <h6>{{ $t("shop.selectAddressP") }}</h6>
          <div v-if="addresses" class="addresses row mt-3">
            <div v-for="(address, index) in addresses" :key="`address_${getAddressId(address) ?? index}`"
              class="address col-md-4">
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.company") }}:</div>
                <div class="col-md-4">
                  {{ address.company_name ?? profile.company }}
                  <div v-if="shouldShowFieldError(address, 'company')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'company', t) }}</small>
                  </div>
                </div>
                <div class="col-md-1">
                  <MDBIcon icon="pen" size="1x" @click="showEditForm(address)" class="cursor--pointer" />
                </div>
                <div class="col-md-1">
                  <MDBIcon icon="trash" size="1x" @click="openRemoveAddressModal(address)" class="cursor--pointer" />
                </div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">
                  {{ $t("shop.contactPerson") }}:
                </div>
                <div class="col-md-8">
                  {{ address.first_name }} {{ address.last_name }}
                  <div v-if="shouldShowFieldError(address, 'firstName')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'firstName', t) }}</small>
                  </div>
                  <div v-if="shouldShowFieldError(address, 'lastName')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'lastName', t) }}</small>
                  </div>
                </div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.streetHouseNr") }}:</div>
                <div class="col-md-8">
                  {{ address.street }}
                  <div v-if="shouldShowFieldError(address, 'street')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'street', t) }}</small>
                  </div>
                </div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.postcode") }}:</div>
                <div class="col-md-8">
                  {{ address.postal_code }}
                  <div v-if="shouldShowFieldError(address, 'postalCode')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'postalCode', t) }}</small>
                  </div>
                </div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.location") }}:</div>
                <div class="col-md-8">
                  {{ address.city }}
                  <div v-if="shouldShowFieldError(address, 'city')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'city', t) }}</small>
                  </div>
                </div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.country") }}:</div>
                <div class="col-md-8">
                  {{
                    address.country
                      ? $t(`account.countries.${address.country.toLowerCase()}`)
                      : "-"
                  }}
                  <div v-if="shouldShowFieldError(address, 'country')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'country', t) }}</small>
                  </div>
                </div>
              </div>
              <div class="row gx-2">
                <div class="col-md-4">{{ $t("account.profile.mobile") }}:</div>
                <div class="col-md-8">
                  {{ address.phone }}
                  <div v-if="shouldShowFieldError(address, 'phone')"
                    class="d-flex align-items-start gap-1 text-danger mt-1">
                    <MDBIcon icon="exclamation-circle" size="1x" iconStyle="fas" class="mt-1 flex-shrink-0" />
                    <small>{{ getAddressFieldMessage(address, profile, 'phone', t) }}</small>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12 my-3">
                  <MDBBtn class="btn" :class="[
                    selectedAddress && String(getAddressId(selectedAddress)) === String(getAddressId(address))
                      ? 'btn-primary'
                      : 'btn-outline-primary',
                  ]" @click="selectAddress(address)" :disabled="hasDeletedItems">
                    {{ $t("global.buttons.selectAddressBtn") }}
                  </MDBBtn>
                </div>
              </div>
            </div>
          </div>
        </MDBCardText>
      </MDBCardBody>
      <MDBCardFooter class="text-muted">
        <MDBBtn color="success" :disabled="!selectedAddress || !overPoints || pending" @click="toOrderSummary">{{
          $t("global.buttons.toOrderSummary") }}</MDBBtn>
        <MDBBtn color="primary" @click="back">{{ $t("global.buttons.back") }}</MDBBtn>
      </MDBCardFooter>
    </MDBCard>
  </MDBContainer>

  <MDBModal v-model="showEditAddress" v-show="showEditAddress" tabindex="-1" centered size="xl" @hide="closeModal">
    <EditAddress :editAddress="editAddress" @close="closeModal()" />
  </MDBModal>
  <RemoveAddressModal v-model="showRemoveAddressModal" :address-label="removeAddressLabel" :pending="pending"
    @confirm="confirmRemoveAddress" />
  <MDBModal v-model="showNewAddressModal" v-show="showNewAddressModal" tabindex="-1" centered size="xl"
    @hide="closeNewAddressModal">
    <NewAddress in-modal @close="closeNewAddressModal" />
  </MDBModal>
</template>

<script setup>
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBCardFooter,
  MDBBtn, MDBIcon, MDBModal
} from "mdb-vue-ui-kit";
import { computed, ref, onMounted, watch, h } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import NewAddress from "@/components/Shop/Order/NewAddress.vue";
import EditAddress from "@/components/Shop/Order/EditAddress.vue";
import RemoveAddressModal from "@/components/Shop/Order/RemoveAddressModal.vue";
import Loader from "@/components/General/Loader/index.vue";
import {
  getIncompleteAddressMessages,
  getMissingAddressFieldKeys,
  isAddressIncomplete,
} from "@/utils/incompleteAddress";

import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useI18n } from 'vue-i18n'

const router = useRouter();
const store = useStore();
const { t } = useI18n()
const selectedAddress = ref(null);
const loading = ref(true)
const showEditAddress = ref(false)
const editAddress = ref(null)
const showRemoveAddressModal = ref(false);
const removeAddressId = ref(null);
const removeAddressLabel = ref("");
const showNewAddressModal = ref(false);
const hasUserSelectedAddress = ref(false);

const addresses = computed(() => store.getters.getUserAddresses);
const profile = computed(() => store.getters.getProfile);
const selectedAddressId = computed(() => store.getters.getSelectedAddress);

const user = computed(() => store.getters.getMe);
const cart = computed(() => store.getters.getCart);
const pending = computed(() => store.getters.getPending);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const overPoints = computed(() => {
  return (user.value?.points?.avail_points ?? 0) >= (cart.value?.total ?? 0);
});

const isItemDeleted = (item) => {
  return Boolean(item?.product?.deleted_at || item?.product_variant?.deleted_at);
};

const getProductName = (product) => {
  if (!product) return "";
  const localizedName = product?.name?.[currentLocale.value];
  if (localizedName) return localizedName;
  const fallbackName =
    product?.name && typeof product.name === "object"
      ? Object.values(product.name).find((value) => value)
      : product?.name;
  return fallbackName || product?.sku || product?.id || "";
};

const deletedItems = computed(() =>
  (cart.value?.items || []).filter((item) => isItemDeleted(item))
);

const deletedItemNames = computed(() =>
  deletedItems.value.map((item) => getProductName(item?.product)).filter((name) => name)
);

const hasDeletedItems = computed(() => deletedItems.value.length > 0);

const getAddressId = (address) => address?.plainId ?? address?.id ?? null;

const isAddressFieldMissing = (address, profileData, fieldKey) =>
  getMissingAddressFieldKeys(address, profileData).includes(fieldKey);

const getAddressFieldMessage = (address, profileData, fieldKey, translate) =>
  translate(`shop.incompleteAddress.${fieldKey}`);

const isSelectedAddress = (address) =>
  selectedAddress.value &&
  String(getAddressId(selectedAddress.value)) === String(getAddressId(address));

const shouldShowAddressValidation = (address) =>
  hasUserSelectedAddress.value && isSelectedAddress(address);

const shouldShowFieldError = (address, fieldKey) =>
  shouldShowAddressValidation(address) && isAddressFieldMissing(address, profile.value, fieldKey);

const clearSelectedAddress = () => {
  selectedAddress.value = null;
  store.dispatch("setAddressSelected", null);
};

const selectAddress = (data) => {
  store.dispatch("setAddressSelected", getAddressId(data));
  selectedAddress.value = data;
  hasUserSelectedAddress.value = true;
};

const back = () => {
  router.go(-1);
};

const toOrderSummary = () => {
  if (isAddressIncomplete(selectedAddress.value, profile.value)) {
    const messages = getIncompleteAddressMessages(selectedAddress.value, profile.value, t);

    toast(
      () =>
        h(
          "div",
          messages.map((message, index) =>
            h("div", { key: index, class: index > 0 ? "mt-1" : undefined }, message)
          )
        ),
      {
        autoClose: 10000,
        type: "error",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      }
    );
  } else {
    router.push({
      name: "orderSummary",
    });
  }
};

const showEditForm = (address) => {
  showEditAddress.value = true
  editAddress.value = { ...address }
}

const openNewAddressModal = () => {
  showNewAddressModal.value = true;
};

const closeNewAddressModal = () => {
  showNewAddressModal.value = false;
};

const openRemoveAddressModal = (address) => {
  removeAddressId.value = address?.plainId ?? null;
  removeAddressLabel.value = [
    address?.company_name ?? profile.value?.company,
    address?.street,
    address?.postal_code,
    address?.city,
  ]
    .filter(Boolean)
    .join(", ");
  showRemoveAddressModal.value = true;
};

const confirmRemoveAddress = async () => {
  if (!removeAddressId.value) {
    showRemoveAddressModal.value = false;
    return;
  }

  await deleteAddress(removeAddressId.value);
  showRemoveAddressModal.value = false;
  removeAddressId.value = null;
  removeAddressLabel.value = "";
};

const deleteAddress = async (id) => {
  const selectedIdBeforeDelete = getAddressId(selectedAddress.value);

  await store.dispatch("deleteAddress", id);
  await store.dispatch("me");

  if (selectedIdBeforeDelete && String(selectedIdBeforeDelete) === String(id)) {
    clearSelectedAddress();
  }
}

const closeModal = () => {
  showEditAddress.value = false
  editAddress.value = null
}

onMounted(async () => {
  await store.dispatch("me");
  await store.dispatch("fetchUserShopCart");

  loading.value = false
});

watch([addresses, selectedAddressId], ([newAddresses, persistedSelectedId]) => {
  const selectedId = persistedSelectedId ?? getAddressId(selectedAddress.value);
  if (!selectedId) return;

  const updatedSelectedAddress = (newAddresses || []).find(
    (address) => String(getAddressId(address)) === String(selectedId)
  );

  if (!updatedSelectedAddress) {
    clearSelectedAddress();
    return;
  }

  selectedAddress.value = updatedSelectedAddress;
});
</script>

<style lang="scss" scoped></style>
